import i18next from 'i18next';
import {
    HttpMethods,
    InternalResourceStatusEnum,
    ResourceStatusEnum
} from '../../Protocol/IHttpInterface';
import {
    ConsentOtpStatusEnum,
    IDocumentType,
    ISendReverseOtpResponseData,
    ISubmitRegistrationRequest,
    IValidateCustomerIdResponseDataType,
    IIdentityDocument
} from '../../Service/IRegistrationService';
import Constants, {
    ISOLanguageCodeEnum,
    LOGGER_TAGS,
    RESEND_REVERSE_OTP_INTERVAL_IN_SECONDS,
    REVERSE_OTP_EXPIRATION_INTERVAL_IN_SECONDS,
    START_CUSTOMER_ACTION_POLLING_WAITING_TIME_IN_MILLISECONDS,
    DOCUMENT_TYPES,
    OTP_OPERATION_TYPE
} from '../../Utils/Constants';
import { ERROR_CODES } from '../../Utils/Constants/Errors';
import EventEmitter from '../../Utils/EventEmitter';
import Logger from '../../Utils/Logger';
import Utils from '../../Utils/Utils';
import { ResourceAvailabilityInformationProps } from '../data/ResourceAvailabilityInformation';
import RegistrationManager from './RegistrationManager';
import { Occupations as PROD_OCCUPATIONS } from '../../Utils/Constants/occupations.prod';
import { Occupations as UAT_OCCUPATIONS } from '../../Utils/Constants/occupations.uat';

class AgentRegistrationManager extends RegistrationManager {
    private _otpAvailabilityInformation!: Omit<ISendReverseOtpResponseData, 'status'>;

    private _ussdAvailabilityInformation!: ResourceAvailabilityInformationProps;

    private _agentWhiteListedResponse: string = '';

    private _setDocumentType?: (docType: IDocumentType) => void;

    public setDocumentTypeCallback(callback: (docType: IDocumentType) => void) {
        this._setDocumentType = callback;
    }

    createAccount(
        isMetadatForm:boolean, isWhitelistedUser: boolean, documentType: string, identityDocuments:IIdentityDocument[]
    ): Promise<any> {
        const registrationData = this.userManager.getUserInformation();
        const premiumExists = this.userManager.getPremiumExists();
        const customerExists = this.userManager.getCustomerExists();
        const normalizeOccupation = (occupation?: string): string | undefined => {
            if (!occupation) return undefined;
            const env = process.env.REACT_APP_ENV?.toLowerCase();
            const source = env === 'uat' || env === 'dev' ? UAT_OCCUPATIONS : PROD_OCCUPATIONS;

            const match = Object.values(source).find(
                (o) => o.EN.toLowerCase() === occupation.toLowerCase() || o.FR.toLowerCase() === occupation.toLowerCase()
            );
            return match ? match.EN : occupation;
        };
        let resolvedDocumentType = documentType.toUpperCase() as IDocumentType;
        const updateDocumentType = (docType: IDocumentType) => {
            this._setDocumentType?.(docType);
            this.documentManager.setDocumentType(docType);
        };
        if (isWhitelistedUser) {
            let selectedLabel: string;
            if (isWhitelistedUser && registrationData?.othersType && documentType === DOCUMENT_TYPES.OTHERS) {
                selectedLabel = registrationData.othersType;
            } else {
                selectedLabel = documentType;
            }
            if (selectedLabel && Array.isArray(identityDocuments)) {
                const match = identityDocuments.find((doc) => doc.en?.toLowerCase() === selectedLabel.toLowerCase()
                || doc.fr?.toLowerCase() === selectedLabel.toLowerCase()
                );

                resolvedDocumentType = match?.documentType.toUpperCase() as IDocumentType
                || (selectedLabel.toUpperCase() as IDocumentType);

                if (registrationData) {
                    registrationData.othersType = selectedLabel;
                }
                this._setDocumentType?.(resolvedDocumentType);
                this.documentManager.setDocumentType(resolvedDocumentType);
                Logger.log(
                    LOGGER_TAGS.AGENT_REGISTRATION_MANAGER,
                    `Whitelisted user selected document: ${selectedLabel}, resolvedDocumentType: ${resolvedDocumentType}`
                );
            } else {
                resolvedDocumentType = selectedLabel.toUpperCase() as IDocumentType;
                this._setDocumentType?.(resolvedDocumentType);
                this.documentManager.setDocumentType(resolvedDocumentType);
            }
        }
        updateDocumentType(resolvedDocumentType);
        const operationType = (customerExists && !premiumExists)
            ? OTP_OPERATION_TYPE.UPDATE_KYC
            : OTP_OPERATION_TYPE.REGISTRATION;
        // Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Final documentType to submit:', resolvedDocumentType);
        let relativeUrl: string;
        if (isMetadatForm) {
            relativeUrl = Constants.API_ENDPOINTS.AGENT.META_ACCOUNT_CREATION;
        } else {
            relativeUrl = Constants.API_ENDPOINTS.AGENT.ACCOUNT_CREATION;
        }

        const submitRegistrationRequest: ISubmitRegistrationRequest = {
            relativeUrl,
            data: {
                msisdn: this.userManager.getMsisdn(),
                resource: {
                    language: ISOLanguageCodeEnum[i18next.language.toUpperCase() as keyof typeof ISOLanguageCodeEnum],
                    ...registrationData,
                    gender: String(this.dataManager.convertToGender(String(registrationData?.gender)))[0].toUpperCase(),
                    documentType: resolvedDocumentType,
                    occupation: normalizeOccupation(registrationData?.occupation),
                    operationType
                }
            }
        };

        Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Request to create account initiated', registrationData);
        const promise = this.registrationService
            .requestAccountCreation(submitRegistrationRequest)
            .then(async (response) => {
                Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Successfully requested account creation', response);
                // this.setAgentWhiteListedResponse(response.status);
                // Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, `Agent Whitelisted user response to: ${this.getAgentWhiteListedResponse()}`);
                this._requestSubmissionStatus = {
                    isCreated: response.status === ResourceStatusEnum.SYSTEM_APPROVING
                };
                this.abortPreviousPollingRequest();
                this.setController(new AbortController());
                return Promise.resolve('');
            },
            (errorCode) => {
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Failed to request account creation', errorCode);
                this._requestSubmissionStatus = {
                    isCreated: false,
                    errorCode: Utils.convertToErrorCode(errorCode)
                };
                return Promise
                    .reject(Utils.convertToErrorCode(errorCode));
            });

        return promise;
    }

    validateAgentPin(pin: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.registrationService.validateAgentPin(String(pin))
                .then((response) => {
                    if (response?.valid) {
                        // Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Successfully validated PIN', response);
                        resolve('');
                    } else {
                        Logger.error(
                            LOGGER_TAGS.AGENT_REGISTRATION_MANAGER,
                            'Failed to validate PIN',
                            response?.error?.additionalFields?.g2ErrorDescription || response?.error?.message || response
                        );
                        reject(Utils.convertToErrorCode(response?.error?.errorCode) || ERROR_CODES.VALIDATE_AGENT_PIN.GENERIC_FAIL);
                    }
                })
                .catch((error) => {
                    Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Failed to validate PIN', error);
                    const errorCode = Utils.convertToErrorCode(error?.errorCode);
                    reject(errorCode || ERROR_CODES.VALIDATE_AGENT_PIN.GENERIC_FAIL);
                });
        });
    }

    async sendReverseOtp(): Promise<Omit<ISendReverseOtpResponseData, 'status'>> {
        try {
            const response = await this.registrationService
                .sendReverseOtp({
                    msisdn: this.userManager.getMsisdn(),
                    language: ISOLanguageCodeEnum[i18next.language.toUpperCase() as keyof typeof ISOLanguageCodeEnum]
                });

            switch (response.status) {
            case InternalResourceStatusEnum.SUCCESS:
                Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Successfully sent reverse otp', response);
                this._otpAvailabilityInformation = {
                    timeLeftForExpiration: REVERSE_OTP_EXPIRATION_INTERVAL_IN_SECONDS,
                    timeLeftForResend: RESEND_REVERSE_OTP_INTERVAL_IN_SECONDS,
                    reverseOtpCode: response.code,
                    uniqueIdentifier: response.uniqueIdentifier,
                    isBlocked: false
                };

                setTimeout(() => this
                    .handleCustomerPollingResult({
                        requestId: response.uniqueIdentifier,
                        controller: this._controller
                    }), START_CUSTOMER_ACTION_POLLING_WAITING_TIME_IN_MILLISECONDS);
                return Promise.resolve(this._otpAvailabilityInformation);
            case InternalResourceStatusEnum.BLOCKED:
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'MSISDN currently blocked', response);
                this._otpAvailabilityInformation = {
                    timeLeftForExpiration: NaN,
                    timeLeftForResend: response?.timeLeft || RESEND_REVERSE_OTP_INTERVAL_IN_SECONDS,
                    reverseOtpCode: '',
                    uniqueIdentifier: '',
                    isBlocked: true
                };
                return Promise.resolve(this._otpAvailabilityInformation);
            default:
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Unexpected resource status found', response);
                this._otpAvailabilityInformation = {
                    timeLeftForExpiration: response?.timeLeftExpired || REVERSE_OTP_EXPIRATION_INTERVAL_IN_SECONDS,
                    timeLeftForResend: response?.timeLeftRetry || RESEND_REVERSE_OTP_INTERVAL_IN_SECONDS,
                    reverseOtpCode: response?.code || '____',
                    uniqueIdentifier: response?.uniqueIdentifier,
                    isBlocked: response.status
                };
            }
            return Promise.reject(this._otpAvailabilityInformation);
        } catch (error: any) {
            Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Failed to send reverse otp', error);
            return Promise.reject(error?.errorCode ? Utils.convertToErrorCode(error.errorCode) : error);
        }
    }

    handleCustomerPollingResult({ requestId, controller }: { requestId: string, controller: AbortController}) {
        Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Polling for customer action initiated', requestId);

        this.fallbackService.requestRecordStatus({
            endpointProps: {
                relativeUrl: `${Constants.API_ENDPOINTS.AGENT.QUERY_CONSENT_TERMS_AND_CONDITIONS_ID_STATUS}/${requestId}`,
                httpMethod: HttpMethods.GET
            },
            requestId,
            fieldName: 'consentResponse',
            successCallback: (data) => {
                Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Customer successfully performed polling validation', data);
                EventEmitter.dispatch(
                    LOGGER_TAGS.HANDLE_POLING_RESULT, false,
                    {
                        success: data.status === ConsentOtpStatusEnum.VALIDATED,
                        data
                    }
                );
            },
            errorCallback: (error: any) => {
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Customer failed to perform polling validation', error);
                if (Utils.convertToErrorCode(error?.errorCode) === ERROR_CODES.VALIDATE_REVERSE_OTP.INVALID) {
                    Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Request dropped with id', requestId);
                }
                EventEmitter.dispatch(
                    LOGGER_TAGS.HANDLE_POLING_RESULT,
                    false,
                    {
                        success: false,
                        errorCode: error?.errorCode
                            ? Utils.convertToErrorCode(error.errorCode)
                            : ERROR_CODES.GENERIC_ERROR
                    }
                );
            },
            onAbortCallback: () => this._controller = new AbortController(),
            controller,
            helperCallback: this.convertReverseOtpToFallbackObject
        });
    }

    async sendUssdPush(): Promise<any> {
        try {
            const response = await this.registrationService
                .sendUssdPush({
                    msisdn: this.userManager.getMsisdn(),
                    language: ISOLanguageCodeEnum[i18next.language.toUpperCase() as keyof typeof ISOLanguageCodeEnum]
                });

            switch (response.status) {
            case ResourceStatusEnum.SUCCEEDED:
                Logger.log(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Successfully sent USSD push', response);
                this._ussdAvailabilityInformation = {
                    timeLeft: response.data.timeLeftRetry,
                    isBlocked: true,
                    exists: response.data.exists
                };

                setTimeout(() => this
                    .handleCustomerPollingResult({
                        requestId: response.data.uniqueIdentifier,
                        controller: this._controller
                    }), START_CUSTOMER_ACTION_POLLING_WAITING_TIME_IN_MILLISECONDS);
                return Promise.resolve(this._otpAvailabilityInformation);
            case InternalResourceStatusEnum.BLOCKED:
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'MSISDN currently blocked', response);
                this._otpAvailabilityInformation = {
                    timeLeftForExpiration: NaN,
                    timeLeftForResend: response?.timeLeft || RESEND_REVERSE_OTP_INTERVAL_IN_SECONDS,
                    reverseOtpCode: '',
                    uniqueIdentifier: '',
                    isBlocked: true
                };
                return Promise.resolve(this._otpAvailabilityInformation);
            default:
                Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Unexpected resource status found', response);
                this._ussdAvailabilityInformation = {
                    timeLeft: response?.data?.timeLeftRetry || 0,
                    isBlocked: Boolean(response?.data?.timeLeftRetry),
                    exists: response.data.exists
                };
            }
            return Promise.reject(this._otpAvailabilityInformation);
        } catch (error: any) {
            Logger.error(LOGGER_TAGS.AGENT_REGISTRATION_MANAGER, 'Failed to send reverse otp', error);
            return Promise.reject(error?.errorCode ? Utils.convertToErrorCode(error.errorCode) : error);
        }
    }

    abortPreviousPollingRequest() {
        if (this._controller) {
            this._controller.abort();
            return;
        }
        this._controller = new AbortController();
    }

    convertReverseOtpToFallbackObject(response: IValidateCustomerIdResponseDataType): IValidateCustomerIdResponseDataType {
        switch (true) {
        case response.data?.status === ConsentOtpStatusEnum.IN_PROGRESS:
        case response.data?.status === ConsentOtpStatusEnum.VALID:
            return { ...response, status: ResourceStatusEnum.PENDING };
        case response.data?.status === ConsentOtpStatusEnum.VALIDATED:
            return { ...response, status: ResourceStatusEnum.SUCCEEDED };

        default: {
            return { ...response, status: ResourceStatusEnum.FAILED };
        }
        }
    }

    getOtpAvailabilityInformation(): Omit<ISendReverseOtpResponseData, 'status'> {
        return this._otpAvailabilityInformation;
    }

    setOtpAvailabilityInformation(value: Omit<ISendReverseOtpResponseData, 'status'>): void {
        this._otpAvailabilityInformation = value;
    }

    getUssdAvailabilityInformation(): ResourceAvailabilityInformationProps {
        return this._ussdAvailabilityInformation;
    }

    setUssdAvailabilityInformation(value: ResourceAvailabilityInformationProps) {
        this._ussdAvailabilityInformation = value;
    }

    getAgentWhiteListedResponse(): string {
        return this._agentWhiteListedResponse;
    }

    setAgentWhiteListedResponse(value: string): void {
        this._agentWhiteListedResponse = value;
    }
}

export default new AgentRegistrationManager();
